import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  NumberInput,
  Toggle,
  InlineNotification,
  Loading,
} from '@carbon/react';
import { LuWifi, LuWifiOff } from 'react-icons/lu';
import { toast } from 'sonner';
import {
  DEFAULT_MQTT_PORT,
  MQTT_BROKER_HOST_HINT,
  emptyMqttConfig,
  isMqttConfigComplete,
} from '../../utils/mqttDefaults';
import {
  connectMqttBroker,
  fetchMqttBrokerConfig,
  checkMqttBrokerHealth,
  formatMqttConnectError,
  MQTT_CONNECT_ERROR_MESSAGE,
} from '../../utils/mqttConnect';

const readOnlyFieldProps = {
  readOnly: true,
  style: { backgroundColor: 'var(--cds-field-02, #f4f4f4)', color: 'var(--cds-text-secondary, #525252)' },
};

const BrokerStatusPill = ({ reachable, loading, host, port }) => {
  if (loading) {
    return (
      <div className="mqtt-modal-status mqtt-modal-status--checking" data-testid="mqtt-broker-status">
        <Loading small withOverlay={false} />
        <span>Checking broker…</span>
      </div>
    );
  }

  if (reachable) {
    return (
      <div className="mqtt-modal-status mqtt-modal-status--online" data-testid="mqtt-broker-status">
        <LuWifi size={18} aria-hidden="true" />
        <span>MQTT Online</span>
        <span className="mqtt-modal-status-host">
          {host || MQTT_BROKER_HOST_HINT}:{port ?? DEFAULT_MQTT_PORT}
        </span>
      </div>
    );
  }

  return (
    <div className="mqtt-modal-status mqtt-modal-status--offline" data-testid="mqtt-broker-status">
      <LuWifiOff size={18} aria-hidden="true" />
      <span>MQTT Offline</span>
    </div>
  );
};

export const MqttBrokerModal = ({
  open,
  onOpenChange,
  mqttConfig,
  onConfigChange,
  onConnected,
  accountManaged = true,
}) => {
  const [connecting, setConnecting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gatewayName, setGatewayName] = useState(null);
  const [brokerReachable, setBrokerReachable] = useState(null);
  const [hasCredentials, setHasCredentials] = useState(false);

  useEffect(() => {
    if (!open) {
      setError(null);
      setBrokerReachable(null);
      setHasCredentials(false);
      return undefined;
    }

    let cancelled = false;

    const loadConfig = async () => {
      setLoading(true);
      setError(null);
      try {
        const [config, health] = await Promise.all([
          fetchMqttBrokerConfig().catch(() => emptyMqttConfig()),
          checkMqttBrokerHealth().catch(() => ({ brokerReachable: false, hasCredentials: false })),
        ]);

        if (!cancelled) {
          onConfigChange({
            host: config.host,
            port: config.port,
            username: config.username,
            password: config.password,
            useTLS: config.useTLS,
          });
          setGatewayName(config.gateway_name || null);
          setBrokerReachable(!!health.brokerReachable);
          setHasCredentials(!!health.hasCredentials || !!(config.username && config.password));
        }
      } catch (err) {
        if (!cancelled) {
          setError(formatMqttConnectError(err?.message || err));
          setBrokerReachable(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadConfig();
    return () => {
      cancelled = true;
    };
  }, [open, onConfigChange]);

  const update = (patch) => {
    if (accountManaged) return;
    onConfigChange({ ...mqttConfig, ...patch });
  };

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    try {
      await connectMqttBroker(mqttConfig);
      setBrokerReachable(true);
      onConnected?.();
      onOpenChange(false);
      toast.success('Connected to MQTT broker');
    } catch (err) {
      const message = formatMqttConnectError(err?.message || err);
      setError(message);
      toast.error(MQTT_CONNECT_ERROR_MESSAGE);
    } finally {
      setConnecting(false);
    }
  };

  const configComplete = isMqttConfigComplete(mqttConfig);
  const displayHost = mqttConfig.host || MQTT_BROKER_HOST_HINT;
  const displayPort = mqttConfig.port ?? DEFAULT_MQTT_PORT;

  return (
    <Modal
      open={open}
      modalHeading="MQTT Broker Connection"
      primaryButtonText={connecting ? 'Connecting...' : 'Connect'}
      secondaryButtonText="Cancel"
      primaryButtonDisabled={connecting || loading || !configComplete}
      onRequestClose={() => !connecting && !loading && onOpenChange(false)}
      onRequestSubmit={handleConnect}
      data-testid="mqtt-modal"
      size="sm"
    >
      <BrokerStatusPill
        reachable={brokerReachable}
        loading={loading}
        host={displayHost}
        port={displayPort}
      />

      {error && (
        <InlineNotification
          kind="error"
          title="Connection failed"
          subtitle={error}
          lowContrast
          style={{ marginBottom: '1rem', maxWidth: '100%' }}
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
        <InlineNotification
          kind="info"
          title="Chillverify MQTT broker"
          subtitle={
            gatewayName
              ? `Credentials from gateway "${gatewayName}". Port defaults to ${DEFAULT_MQTT_PORT} when not set.`
              : `TBMQ username and password load from your gateway record. Port defaults to ${DEFAULT_MQTT_PORT}.`
          }
          lowContrast
          hideCloseButton
          style={{ maxWidth: '100%' }}
        />

        {!loading && !hasCredentials && (
          <InlineNotification
            kind="warning"
            title="No MQTT credentials found"
            subtitle="Register your gateway to generate MQTT credentials, then open this modal again."
            lowContrast
            hideCloseButton
            style={{ maxWidth: '100%' }}
          />
        )}

        <TextInput
          id="mqtt-host"
          labelText="Broker host"
          placeholder={MQTT_BROKER_HOST_HINT}
          helperText="mqtt_broker_host from your gateway"
          value={displayHost}
          onChange={(e) => update({ host: e.target.value })}
          data-testid="mqtt-host"
          {...(accountManaged ? readOnlyFieldProps : {})}
        />

        <NumberInput
          id="mqtt-port"
          label="Port"
          helperText={`mqtt_broker_port from gateway, or ${DEFAULT_MQTT_PORT} by default`}
          value={displayPort}
          min={1}
          max={65535}
          disabled={accountManaged}
          onChange={(_, { value }) => {
            if (value !== undefined && value !== '') update({ port: Number(value) });
          }}
          data-testid="mqtt-port"
        />

        {!accountManaged && (
          <Toggle
            id="mqtt-tls"
            labelText="Use TLS/SSL"
            toggled={!!mqttConfig.useTLS}
            onToggle={(checked) => {
              update({ useTLS: checked });
            }}
            data-testid="mqtt-tls-toggle"
          />
        )}

        <TextInput
          id="mqtt-username"
          labelText="TBMQ username"
          helperText="tbmq_username from Supabase"
          placeholder="Not set on gateway"
          value={mqttConfig.username || ''}
          onChange={(e) => update({ username: e.target.value })}
          data-testid="mqtt-username"
          {...(accountManaged ? readOnlyFieldProps : {})}
        />

        <TextInput
          id="mqtt-password"
          type="password"
          labelText="TBMQ password"
          helperText="tbmq_password from Supabase"
          placeholder="Not set on gateway"
          value={mqttConfig.password || ''}
          onChange={(e) => update({ password: e.target.value })}
          data-testid="mqtt-password"
          {...(accountManaged ? readOnlyFieldProps : {})}
        />
      </div>
    </Modal>
  );
};

export default MqttBrokerModal;
